import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";

import SaveCancel from "../../../components/SaveCancel";
import useUser from "../../../hooks/useUser";
import UserFields from "./components/UserFields";
import TabPanel from "../../../components/TabPanel";
import { a11yProps } from "../../../utils/common";
import PlanDetails from "./components/PlanDetails";
import BranchAndOthers from "./components/BranchAndOthers";
import IpTvPlan from "./components/IpTvPlan";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import useAuth from "../../../hooks/useAuth";

import AddressDetail from "../../../components/AddressDetail";
import InstallationAddressDetail from "../../../components/InstallationAddressDetail";
import AssignedPlan from "./components/AssignedPlan";
import UserKycTab from "./components/UserKycTab";
import useKycDocument from "../../../hooks/useKycDocument";
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

const initialValues = {
  userName: "",
  mobileNo: "",
  emailId: "",
  fullName: "",
  gstNo: "",
  doorNo: "",
  pinCode: "",
  area: "",
  city: "",
  state: "",
  country: "",
  insDoorNo: "",
  insPinCode: "",
  insArea: "",
  insCity: "",
  insState: "",
  insCountry: "",
  tenant: {},
  branch: {},
  plan: {},
  rate: {},
  serialNo: "",
  routerPassword: "",
  appPassword: "",
  userServiceType: "INTERNET",
  // ipTvPlanId: "",
  // ipTvPlan: {},
  status: "ACTIVE",
  sameAsPresentAddress: 0,
  connectionType: "HOME_USER",
  accountType: "NORMAL_USER",
  country: "",
  ottPlanCode: null,
  caf: null,
  isCafLinked: false,
  createCaf: false,
  idProofType: "",
  idProofNumber: "",
  idProofFile: null,
  addressProofType: "",
  addressProofNumber: "",
  addressProofFile: null,
  profileProofFile: null,
};

const validationSchema = Yup.object().shape({
  tenant: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Tenant is required"),
  branch: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Branch is required"),
  plan: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Plan is required"),
  rate: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Rate is required"),
  userName: Yup.string()
    .required("Required")
    .matches(/^[a-zA-Z0-9,@#*._-]+$/, "Please enter valid username"),
  mobileNo: Yup.string()
    .required("Required")
    .matches(/^\d{10}$/, "Must be a valid phone number with up to 10 digits"),
  emailId: Yup.string("Enter email id")
    .required("Email id is required")
    .email("Enter a valid email"),
  routerPassword: Yup.string().max(255).required("Router password is required"),
  confirmRouterPassword: Yup.string()
    .required("Router password is required")
    .when("routerPassword", {
      is: (val) => (val && val.length > 0 ? true : false),
      then: (schema) =>
        Yup.string().oneOf([Yup.ref("routerPassword")], "Both password need to be the same"),
    }),
  appPassword: Yup.string()
    .matches(passwordRules, {
      message:
        "Please create a stronger password, Password must contain min 8 char, " +
        " 1 upper case letter, 1 lower case letter, 1 numeric digit",
    })
    .required("Required"),
  confirmAppPassword: Yup.string()
    .required("App password is required")
    .when("appPassword", {
      is: (val) => (val && val.length > 0 ? true : false),
      then: (schema) =>
        Yup.string().oneOf([Yup.ref("appPassword")], "Both password need to be the same"),
    }),
  fullName: Yup.string().required("Required"),
  doorNo: Yup.string().required("Required"),
  city: Yup.string().required("Required"),
  state: Yup.string().required("Required"),
  country: Yup.string().required("Required"),
  pinCode: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  insDoorNo: Yup.string().required("Required"),
  insCity: Yup.string().required("Required"),
  insState: Yup.string().required("Required"),
  insCountry: Yup.string().required("Required"),
  insPinCode: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  // installationAddress: Yup.string().required("Required"),
  userServiceType: Yup.string().required("Required"),
  connectionType: Yup.string().required("Required"),
  accountType: Yup.string().required("Required"),
  status: Yup.string().required("Required"),
  // ipTvPlan: Yup.object()
  //   .nullable()
  //   .when("userServiceType", {
  //     is: (val) => (val && val == "COMBO" ? true : false),
  //     then: (schema) =>
  //       Yup.object()
  //         .shape({
  //           label: schema.required(),
  //           value: schema.required(),
  //         })
  //         .nullable()
  //         .required("IpTv Plan is required"),
  //   }),
  // serialNo: Yup.string().when("userServiceType", {
  //   is: (val) => (val && val == "COMBO" ? true : false),
  //   then: (schema) => schema.required("Serial no is required"),
  // }),
  mac: Yup.string()
    .matches(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/, "Invalid MAC address format")
    .when("accountType", {
      is: (val) => (val && val == "MAC_USER" ? true : false),
      then: (schema) => schema.required("Mac is required"),
    }),
  caf: Yup.object()
    .nullable()
    .when("isCafLinked", {
      is: true,
      then: () =>
        Yup.object({
          label: Yup.string().required("CAF is required"),
          value: Yup.mixed().required("CAF is required"),
        })
          .nullable()
          .required("CAF is required"),
      otherwise: () => Yup.mixed().nullable(),
    }),
  idProofType: Yup.string().when("createCaf", {
    is: true,
    then: (schema) => schema.required("Identity Proof Type is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  idProofNumber: Yup.string().nullable(),
  idProofFile: Yup.mixed()
    .when("createCaf", {
      is: true,
      then: (schema) => schema.required("Identity Proof File is required"),
      otherwise: (schema) => schema.nullable(),
    })
    .test("fileFormat", "Only support pdf and image file", (value) => {
      if (!value) return true;
      return ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(value.type);
    }),
  addressProofType: Yup.string().when("createCaf", {
    is: true,
    then: (schema) => schema.required("Address Proof Type is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  addressProofNumber: Yup.string().nullable(),
  addressProofFile: Yup.mixed()
    .when("createCaf", {
      is: true,
      then: (schema) => schema.required("Address Proof File is required"),
      otherwise: (schema) => schema.nullable(),
    })
    .test("fileFormat", "Only support pdf and image file", (value) => {
      if (!value) return true;
      return ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(value.type);
    }),
  profileProofFile: Yup.mixed()
    .when("createCaf", {
      is: true,
      then: (schema) => schema.required("Profile Picture is required"),
      otherwise: (schema) => schema.nullable(),
    })
    .test("fileFormat", "Only support image file", (value) => {
      if (!value) return true;
      return ["image/jpeg", "image/jpg", "image/png"].includes(value.type);
    }),
});

const useTabStyles = styled({
  root: {
    justifyContent: "center",
    width: "1000px",
  },
  scroller: {
    flexGrow: "0",
  },
});

function AddUser() {
  const [planGroupPlanOptions, setPlanGroupPlanOptions] = useState([]);
  const [ratesMap, setRatesMap] = useState({});
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });
  const classes = useTabStyles();

  const { addUser, hasError, errorMessage, isSubmitting } = useUser();
  const { uploadFile } = useKycDocument();
  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: { errors },
    watch,
    control,
    setValue,
  } = methods;

  useEffect(() => {
    console.log(user, "useruserusersss");
    if (
      user &&
      (user.businessType == "BULK_BANDWIDTH" || user.businessType == "LOCAL_CABLE_OPERATOR")
    ) {
      setValue("tenant", {
        label: user.name,
        value: user.tenantId,
      });
    }
  }, [user]);

  console.log(errors, "errorserrorserrors");

  const onSubmit = async (data) => {
    try {
      const isCafLinked = data.isCafLinked;
      const idProofFile = data.idProofFile;
      const idProofType = data.idProofType;
      const idProofNumber = data.idProofNumber;
      const addressProofFile = data.addressProofFile;
      const addressProofType = data.addressProofType;
      const addressProofNumber = data.addressProofNumber;
      const profileProofFile = data.profileProofFile;

      // Keep proof numbers in data so backend receives them
      data.aadharCardNumber = idProofNumber || null;
      data.addressProofNo = addressProofNumber || null;

      delete data.idProofFile;
      delete data.idProofType;
      delete data.idProofNumber;
      delete data.addressProofFile;
      delete data.addressProofType;
      delete data.addressProofNumber;
      delete data.profileProofFile;

      const rateDto = {
        rateId: data.rate.value,
      };
      const planDto = {
        planId: data.plan.value,
        planName: data.plan.label,
      };
      const tenantDto = {
        tenantId: data.tenant.value,
        tenantName: data.tenant.label,
      };
      if (data.tenant != null) {
        data["tenant"] = tenantDto;
      }
      if (data.ipTvPlan != null) {
        data["ipTvPlanId"] = data.ipTvPlan.value;
      }
      if (data.userServiceType === "INTERNET") {
        delete data["ipTvPlanId"];
        delete data["serialNo"];
        delete data["ipTvPlan"];
      }
      data["pinCode"] = data.pinCode?.label;
      data["insPinCode"] = data.insPinCode?.label;
      // console.log(data, "datadatadata122bef");
      // data["taluk"] = {
      //   talukId: data?.talukId,
      // };
      // data["district"] = {
      //   districtId: data?.districtId,
      // };
      // data["state"] = {
      //   stateId: data.state.value,
      //   stateName: data.state.label,
      // };
      // data["country"] = {
      //   countryId: data.country.value,
      //   countryName: data.country.label,
      // };
      // data["city"] = {
      //   cityId: data.city.value,
      //   cityName: data.city.label,
      // };
      // data["pincode"] = {
      //   pincodeId: data.pincode.value,
      //   pincodeName: data.pincode.label,
      // };
      data["rate"] = rateDto;
      data["plan"] = planDto;
      data["branch"] = {
        branchId: data.branch.value,
      };
      data["userType"] = "REGULAR";
      const selectedPlans = planGroupPlanOptions.filter((plan) =>
        watch(`assigned-plan-${plan.value}`),
      );

      if (data.caf != null) {
        data["caf"] = {
          cafId: data.caf.value,
          cafName: data.caf.label,
        };
      }

      const selectedRates = {};
      selectedPlans.forEach((plan) => {
        const rates = ratesMap[plan.value]?.filter((rate) => watch(`assigned-rate-${rate.value}`));
        selectedRates[plan.value] = rates || [];
      });
      let radUserPlans = [];

      for (let key in selectedRates) {
        //console.log("key--------", selectedRates[key]);
        for (let rateIndex in selectedRates[key]) {
          radUserPlans.push({
            plan: {
              planId: key,
              planName: "",
            },
            rate: {
              rateId: selectedRates[key][rateIndex]["value"],
              rateName: selectedRates[key][rateIndex]["label"],
            },
          });
        }
      }
      console.log(data, "user data details");
      data["assignedPlans"] = radUserPlans;
      const createdUser = await addUser(data);
      const radUserId = createdUser?.radUserId;

      if (radUserId && (!isCafLinked || data.createCaf)) {
        // Upload ID Proof
        if (idProofFile) {
          const formData = new FormData();
          formData.append("file", idProofFile);
          formData.append("radUserId", radUserId);
          formData.append("title", "Id Proof");
          formData.append("documentType", idProofType || "AADHAAR");
          await uploadFile(formData);
        }

        // Upload Address Proof
        if (addressProofFile) {
          const formData = new FormData();
          formData.append("file", addressProofFile);
          formData.append("radUserId", radUserId);
          formData.append("title", "Address Proof");
          formData.append("documentType", addressProofType || "AADHAAR");
          await uploadFile(formData);
        }

        // Upload Profile Proof
        if (profileProofFile) {
          const formData = new FormData();
          formData.append("file", profileProofFile);
          formData.append("radUserId", radUserId);
          formData.append("title", "Profile Proof");
          formData.append("documentType", "PROFILE_PIC");
          await uploadFile(formData);
        }
      }

      dispatch(showSuccessSnackbar("User added successfully!"));
      // setStatus({ sent: true });
      reset();
    } catch (err) {
      // setStatus({ sent: false });
      // setErrorSubmit({ submit: err.message });
      //console.log(err, "1111111111ss111");
      let messageDetails = [];
      if (err.errors != undefined) {
        let errors = err.errors;
        for (let error in errors) {
          messageDetails.push(errors[error].field + " " + errors[error].message);
        }
        dispatch(showErrorSnackbar(messageDetails.join(",")));
      } else {
        dispatch(showErrorSnackbar(err.message));
      }
      //dispatch(showErrorSnackbar(err.message));
    }
  };

  const onError = (errors) => {
    console.log(errors, "Validation Errors");

    const kycFields = [
      "idProofType",
      "idProofFile",
      "idProofNumber",
      "addressProofType",
      "addressProofFile",
      "addressProofNumber",
      "profileProofFile",
    ];
    const userDetailFields = [
      "tenant",
      "userName",
      "fullName",
      "mobileNo",
      "emailId",
      "connectionType",
      "accountType",
      "status",
      "caf",
      "mac",
      "userServiceType",
    ];
    const addressFields = [
      "doorNo",
      "city",
      "state",
      "country",
      "pinCode",
      "insDoorNo",
      "insCity",
      "insState",
      "insCountry",
      "insPinCode",
    ];
    const planFields = ["plan", "rate"];
    const branchFields = [
      "branch",
      "routerPassword",
      "confirmRouterPassword",
      "appPassword",
      "confirmAppPassword",
    ];

    const errorKeys = Object.keys(errors);

    if (errorKeys.some((key) => kycFields.includes(key))) {
      setTabValue(5);
      dispatch(showErrorSnackbar("Please complete the required KYC fields."));
    } else if (errorKeys.some((key) => userDetailFields.includes(key))) {
      setTabValue(0);
      dispatch(showErrorSnackbar("Please fill all required fields in User Details tab."));
    } else if (errorKeys.some((key) => addressFields.includes(key))) {
      setTabValue(1);
      dispatch(showErrorSnackbar("Please fill all required fields in Address tab."));
    } else if (errorKeys.some((key) => planFields.includes(key))) {
      setTabValue(2);
      dispatch(showErrorSnackbar("Please fill all required fields in Plan tab."));
    } else if (errorKeys.some((key) => branchFields.includes(key))) {
      setTabValue(3);
      dispatch(showErrorSnackbar("Please fill all required fields in Branch and Others tab."));
    } else {
      dispatch(showErrorSnackbar("Please fix the validation errors in the form."));
    }
  };

  const cancelBtn = () => {
    navigate("/user/userlist/list");
  };

  const presentAddressFields = watch(["doorNo", "pinCode", "area", "city", "state", "country"]);

  const Plan = watch("plan");
  const caf = watch("caf");

  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  console.log(tabValue, "talllah");

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>User</Typography>

        <Typography>Add User</Typography>
      </Breadcrumbs>
    );
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("add_user")} subheader={breadCrump()} />
          <CardContent>
            {/* {status && status.sent && (
              <Alert severity="success" my={3}>
                User added successfully!
              </Alert>
            )}
            {errors && errors.submit && (
              <Alert severity="error" my={3}>
                {errors.submit}
              </Alert>
            )} */}

            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit, onError)}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    classes={{ scroller: classes.scroller }}
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    aria-label="User"
                    sx={{
                      justifyContent: "center",
                      width: {
                        xs: "100%", // Full width on mobile devices
                        sm: "500px", // Fixed width on small devices
                        md: "700px", // Wider on medium devices
                        lg: "900px", // Even wider on larger devices
                      },
                      minWidth: "300px", // Ensure a minimum width across all devices
                      maxWidth: "100%", // Ensure it doesn't overflow the container
                    }}
                  >
                    {" "}
                    <Tab label="User Details" {...a11yProps(0)} />
                    <Tab label="Address " {...a11yProps(1)} />
                    <Tab label="Plan" {...a11yProps(2)} />
                    <Tab label="Branch and Others" {...a11yProps(3)} />
                    <Tab label="Assigned Plan" {...a11yProps(4)} />
                    <Tab label="KYC" {...a11yProps(5)} />
                    {/* <Tab label="IpTv Plan" {...a11yProps(6)} /> */}
                  </Tabs>
                  <TabPanel value={tabValue} index={0}>
                    <UserFields validationFields={validationSchema.fields} hideFields={[]} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <AddressDetail
                      presentAddressFields={presentAddressFields}
                      validationFields={validationSchema.fields}
                      oldAddress={null}
                      cafEnabled={caf != null && Object.keys(caf).length > 0}
                      allowAddPincode
                    />
                  </TabPanel>

                  <TabPanel value={tabValue} index={2}>
                    <PlanDetails validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={3}>
                    <BranchAndOthers validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={4}>
                    <AssignedPlan
                      validationFields={null}
                      Plan={Plan}
                      disabled={true}
                      planGroupPlanOptions={planGroupPlanOptions}
                      setPlanGroupPlanOptions={setPlanGroupPlanOptions}
                      ratesMap={ratesMap}
                      setRatesMap={setRatesMap}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={5}>
                    <UserKycTab caf={caf} />
                  </TabPanel>
                  {/* <TabPanel value={tabValue} index={6}>
                    <IpTvPlan validationFields={validationSchema.fields} />
                  </TabPanel> */}
                </Box>
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default AddUser;
